import { useEffect, useState } from "react";

/**
 * Public webhook signature docs. The canonical HTML lives on the backend under
 * `/api/v1/docs/webhooks` (the only path the ingress actually routes to
 * FastAPI — `/docs/*` is a frontend route). We fetch it and render inline so
 * the URL stays `/docs/webhooks` while the copy stays in one file on the
 * server.
 */
export default function WebhooksDocsPage() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const docsUrl = `${base}/api/v1/docs/webhooks`;
  const [html, setHtml] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(docsUrl, { credentials: "omit" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (alive) setHtml(text);
      })
      .catch((e) => {
        if (alive) setErr(e.message || "Failed to load docs");
      });
    return () => {
      alive = false;
    };
  }, [docsUrl]);

  if (err) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200"
        data-testid="webhooks-docs-error"
      >
        <div className="max-w-md space-y-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-sm">
          <div className="font-semibold text-red-300">
            Could not load webhook docs
          </div>
          <div className="text-slate-400">{err}</div>
          <a
            href={docsUrl}
            className="inline-block text-cyan-300 underline"
            data-testid="webhooks-docs-fallback-link"
          >
            Open raw docs
          </a>
        </div>
      </div>
    );
  }

  if (!html) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-300"
        data-testid="webhooks-docs-loading"
      >
        <div className="text-sm">Loading webhook signature docs…</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-slate-950"
      data-testid="webhooks-docs-page"
    >
      <iframe
        title="Webhook signature docs"
        srcDoc={html}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        className="h-screen w-full border-0 bg-white"
        data-testid="webhooks-docs-frame"
      />
    </div>
  );
}
